import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

// MUI
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';

// common
import { AppState } from '@redux/store';
import useLocalStorage from 'common/hook/useLocalStorage';
import useIsGuest from 'common/hook/useIsGuest';
import useMonthlyMenuFrames from 'common/hook/useMonthlyMenuFrames';
import useGetListClients from 'modules/account/services/useGetListClients';
import { Client } from 'modules/account/types';

// screen
import useMenuDishStatistics from 'modules/dashboard/services/useMenuDishStatistics';
import useAccessStatistics from 'modules/dashboard/services/useAccessStatistics';
import DashboardFilter from 'modules/dashboard/components/DashboardFilter';
import SupplierCountCards from 'modules/dashboard/components/SupplierCountCards';
import MenuUsageCards from 'modules/dashboard/components/MenuUsageCards';
import CatalogDonut from 'modules/dashboard/components/CatalogDonut';
import TopDishesPanel from 'modules/dashboard/components/TopDishesPanel';
import AccessSummaryCards from 'modules/dashboard/components/AccessSummaryCards';
import AccessVisitCards from 'modules/dashboard/components/AccessVisitCards';
import AccessDailyChart from 'modules/dashboard/components/AccessDailyChart';

interface AppliedFilter {
  customerOperationCode: string;
  monthKey: string;
}

const DashboardPage = () => {
  // Danh sách trường phải GIỐNG "Bản đồ thực đơn" (NewMap dùng listClients không type → type=1),
  // KHÔNG phải danh sách của QR NCC TPAT (suppliers dùng type=2).
  const { data: clients = [] } = useGetListClients();

  const [selectedClient, setSelectedClient] = useLocalStorage<Client | null>(
    'dashboard-selected-client',
    null
  );
  // Tháng lấy từ khung thực đơn của năm học (MenuPlan) thay cho tháng của năm dương lịch.
  // Giữ dạng `YYYY-MM` — đúng thứ API thống kê nhận (month_key), và năm luôn đi kèm tháng.
  const { monthOptions, defaultMonthKey } = useMonthlyMenuFrames();
  const [monthKey, setMonthKey] = useState<string>('');
  const month = monthKey || defaultMonthKey || '';
  const [nccStatus, setNccStatus] = useState<string>('available');
  const [applied, setApplied] = useState<AppliedFilter | null>(null);

  // Nhóm người dùng: client (khách hàng/phụ huynh) bị khoá về trường của họ;
  // govAgency (CQQL) và system (HVS) được chọn trường tự do.
  const userAuthen = useSelector((s: AppState) => s.auth.userInfo?.userAuthen);
  const isClientPersona = userAuthen?.userActualManagingOrgType === 'client';
  const myOrgId = userAuthen?.userActualManagingOrgId;

  // Khách: không được xem danh sách khách hàng (BE chặn /external/kios/client) nên không có bộ lọc
  // trường; tự áp tháng hiện tại và tải luôn chỉ số toàn hệ thống (API trả `ncc` + `global_catalog`
  // khi customer_operation_code rỗng; `customer_usage`/`top_dishes` = null nên ẩn 2 khối đó).
  const isGuest = useIsGuest();

  useEffect(() => {
    if (!isGuest) return;
    setApplied({ customerOperationCode: '', monthKey: month });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, month]);

  // Client: tự chọn đúng trường của họ (khoá dropdown).
  useEffect(() => {
    if (!isClientPersona || clients.length === 0) return;
    const mine = clients.find((c) => c.operationCode === myOrgId) ?? clients[0];
    if (mine && mine.operationCode !== selectedClient?.operationCode) {
      setSelectedClient(mine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientPersona, clients, myOrgId, selectedClient?.operationCode]);

  // Client: không đổi được trường nên tự tải dữ liệu theo trường + tháng.
  useEffect(() => {
    if (isClientPersona && selectedClient?.operationCode) {
      setApplied({
        customerOperationCode: selectedClient.operationCode,
        monthKey: month,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientPersona, selectedClient?.operationCode, month]);

  // Tài khoản chọn được trường (CQQL / HVS): mở màn là có số liệu, không chờ bấm Tìm kiếm.
  // Một trường → trường đó. Từ hai trường trở lên → trường đứng đầu danh sách.
  // Chỉ chạy một lần lúc mở; bấm làm mới vẫn xóa trắng như cũ, không tự chọn lại.
  const hasAutoAppliedRef = useRef(false);
  useEffect(() => {
    if (isGuest || isClientPersona || !userAuthen) return;
    if (hasAutoAppliedRef.current) return;
    if (clients.length === 0 || !month) return;

    const client = clients[0];
    if (client.operationCode !== selectedClient?.operationCode) {
      setSelectedClient(client);
    }
    setApplied({
      customerOperationCode: client.operationCode,
      monthKey: month,
    });
    hasAutoAppliedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, isClientPersona, userAuthen, clients, month, selectedClient?.operationCode]);

  /** Ô chọn trường bị khóa: chỉ có đúng một trường để xem, không đổi được. */
  const isClientSelectLocked = isClientPersona || isGuest;

  const { data, refetch } = useMenuDishStatistics({
    customerOperationCode: applied?.customerOperationCode,
    monthKey: applied?.monthKey,
  });
  // Thống kê lượt truy cập không phụ thuộc trường, chỉ theo tháng đang áp dụng.
  const { data: accessData } = useAccessStatistics({ monthKey: applied?.monthKey });

  // Chỉ hiển thị dữ liệu khi đã áp dụng bộ lọc (sau Tìm kiếm).
  // Reset/chưa tìm → applied=null → xóa sạch toàn bộ dữ liệu bên dưới
  // (không phụ thuộc data cũ còn trong cache React Query).
  const shownData = applied ? data : null;
  const shownAccessData = applied ? accessData : null;

  const handleSearch = () => {
    // Không chọn khách vẫn tìm được: pie chart (global) + NCC/HSNL toàn hệ thống vẫn hiển thị.
    setApplied({
      customerOperationCode: selectedClient?.operationCode ?? '',
      monthKey: month,
    });
  };

  /**
   * Đổi trường là áp ngay. Nếu chỉ đổi ô chọn rồi quên bấm Tìm kiếm, màn vẫn hiện số liệu
   * của trường vừa xem — dễ tưởng đó là số của trường mới.
   * Xóa trắng ô trường thì gỡ số liệu, không giữ lại số của trường trước.
   */
  const handleSelectClient = (client: Client | null) => {
    setSelectedClient(client);
    if (isGuest || isClientPersona) return;
    if (!client?.operationCode) {
      setApplied(null);
      return;
    }
    setApplied({
      customerOperationCode: client.operationCode,
      monthKey: month,
    });
  };

  /** Đổi tháng cũng áp ngay, cùng lý do với đổi trường: ô lọc và số liệu không được lệch nhau. */
  const handleChangeMonth = (nextMonthKey: string) => {
    setMonthKey(nextMonthKey);
    if (isGuest || isClientPersona) return;
    if (!selectedClient?.operationCode) return;
    setApplied({
      customerOperationCode: selectedClient.operationCode,
      monthKey: nextMonthKey,
    });
  };

  /**
   * Nút bên phải ô Tìm kiếm.
   *
   * Tài khoản chọn được nhiều trường: xóa trắng bộ lọc như cũ.
   *
   * Tài khoản chỉ xem được MỘT trường (khách hàng/phụ huynh, và Khách) thì ô chọn trường bị khóa —
   * xóa trắng là hỏng hẳn: trường đã mất mà ô lại khóa nên không ai chọn lại được, màn hình trắng
   * vĩnh viễn. Với các tài khoản đó nút này TẢI LẠI dữ liệu chứ không đặt lại tham số.
   */
  const handleReset = () => {
    if (isClientSelectLocked) {
      // Áp lại đúng tham số đang có (thường trùng `applied` nên không đổi queryKey) rồi gọi lại API.
      setApplied({
        customerOperationCode: selectedClient?.operationCode ?? '',
        monthKey: month,
      });
      refetch();
      return;
    }

    setSelectedClient(null);
    setMonthKey(''); // rỗng = quay về tháng mặc định (khung mới nhất)
    setNccStatus('available');
    setApplied(null);
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
      <DashboardFilter
        clients={isGuest ? [] : clients}
        selectedClient={selectedClient}
        onSelectClient={handleSelectClient}
        monthKey={month}
        monthOptions={monthOptions}
        onChangeMonth={handleChangeMonth}
        nccStatus={nccStatus}
        onChangeNccStatus={setNccStatus}
        onSearch={handleSearch}
        onReset={handleReset}
        disableClientSelect={isClientSelectLocked}
      />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))',
          gap: 2,
          mt: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SupplierCountCards data={shownData} />
          {!isGuest && <MenuUsageCards data={shownData} />}
          <CatalogDonut data={shownData} />
          <AccessSummaryCards data={shownAccessData} />
        </Box>
        {!isGuest && <TopDishesPanel data={shownData} />}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <AccessVisitCards data={shownAccessData} />
        <AccessDailyChart data={shownAccessData} />
      </Box>
    </Box>
  );
};

export default DashboardPage;
